// src/phase4/ui/TowerSelectPanel.ts
// Tower Selection Radial Menu - Pure Phaser UI component
// Displays tower options in a circular layout around the base when clicked

import Phaser from 'phaser';
import { TowerType } from '../types/tower';

// Tower option configuration
interface TowerOption {
  type: TowerType;
  name: string;
  cost: number;
  description: string;
  color: number;
  dps: number;
  range: number;
  fireRate: number;
  icon?: string;
}

const TOWER_OPTIONS: TowerOption[] = [
  {
    type: TowerType.CPU_MAIN,
    name: 'CPU Core',
    cost: 100,
    description: 'High DPS',
    color: 0x5890e0,
    dps: 10,
    range: 120,
    fireRate: 1000,
  },
  {
    type: TowerType.LANGUAGE_SIDE,
    name: 'Lang Stack',
    cost: 80,
    description: 'Crit boost',
    color: 0x52c07a,
    dps: 8,
    range: 100,
    fireRate: 1200,
  },
  {
    type: TowerType.NETWORK_SIDE,
    name: 'Net Stack',
    cost: 60,
    description: 'Slow debuff',
    color: 0xe6b832,
    dps: 5,
    range: 180,
    fireRate: 1500,
  },
];

export class TowerSelectPanel extends Phaser.GameObjects.Container {
  private baseId: string = '';
  private onSelectTower?: (baseId: string, type: TowerType) => void;
  private onCancel?: () => void;
  private optionNodes: RadialNode[] = [];
  private menuRadius = 90;
  private centerCircle?: Phaser.GameObjects.Graphics;
  private outerRing?: Phaser.GameObjects.Graphics;
  private isExpanded = false;

  constructor(scene: Phaser.Scene, x: number, y: number) {
    super(scene, x, y);
    scene.add.existing(this);
    this.createMenu();
    this.setVisible(false);
    this.setDepth(1000);
  }

  private createMenu(): void {
    // Outer ring (dashed circle)
    this.outerRing = this.scene.add.graphics();
    this.add(this.outerRing);

    // Center indicator (base position)
    this.centerCircle = this.scene.add.graphics();
    this.add(this.centerCircle);

    // Create radial nodes for each tower option
    const angleStep = (Math.PI * 2) / TOWER_OPTIONS.length;
    TOWER_OPTIONS.forEach((option, index) => {
      const angle = angleStep * index - Math.PI / 2; // Start from top
      const node = new RadialNode(
        this.scene,
        option,
        angle,
        this.menuRadius,
        (type) => this.handleTowerSelect(type)
      );
      this.add(node);
      this.optionNodes.push(node);
    });

    // Draw initial state
    this.drawCenterCircle(0xe6a640, 0.3);
    this.drawOuterRing(0x2a2824, 1);
  }

  private handleTowerSelect(type: TowerType): void {
    this.onSelectTower?.(this.baseId, type);
    this.hide();
  }

  private drawCenterCircle(color: number, alpha: number): void {
    if (!this.centerCircle) return;
    this.centerCircle.clear();
    this.centerCircle.fillStyle(color, alpha);
    this.centerCircle.fillCircle(0, 0, 20);
    this.centerCircle.lineStyle(2, 0xe6a640, 0.8);
    this.centerCircle.strokeCircle(0, 0, 20);
  }

  private drawOuterRing(color: number, alpha: number): void {
    if (!this.outerRing) return;
    this.outerRing.clear();
    this.outerRing.lineStyle(2, color, alpha);
    this.outerRing.strokeCircle(0, 0, this.menuRadius);

    // Dashed effect
    const dashLength = 8;
    const gapLength = 4;
    const circumference = Math.PI * 2 * this.menuRadius;
    const totalDashes = Math.floor(circumference / (dashLength + gapLength));

    for (let i = 0; i < totalDashes; i++) {
      const startAngle = (i / totalDashes) * Math.PI * 2;
      const endAngle = startAngle + (dashLength / this.menuRadius);
      this.outerRing.arc(0, 0, this.menuRadius, startAngle, endAngle, false);
    }
  }

  show(baseId: string, x: number, y: number): void {
    this.baseId = baseId;
    this.setPosition(x, y);
    this.setVisible(true);
    this.isExpanded = false;

    // Reset all nodes
    this.optionNodes.forEach(node => node.reset());

    // Draw compact center
    this.drawCenterCircle(0xe6a640, 0.3);
    this.drawOuterRing(0x2a2824, 0.3);

    // Scale up animation
    this.setScale(0);
    this.scene.tweens.add({
      targets: this,
      scaleX: { from: 0, to: 1 },
      scaleY: { from: 0, to: 1 },
      duration: 200,
      ease: 'Back.easeOut',
      onComplete: () => this.expandMenu(),
    });
  }

  private expandMenu(): void {
    if (this.isExpanded) return;
    this.isExpanded = true;

    // Draw full outer ring
    this.drawOuterRing(0xe6a640, 0.6);

    // Expand each node with stagger
    this.optionNodes.forEach((node, index) => {
      this.scene.time.delayedCall(index * 50, () => node.expand());
    });
  }

  hide(): void {
    // Contract all nodes first
    this.optionNodes.forEach(node => node.contract());

    this.scene.time.delayedCall(200, () => {
      this.scene.tweens.add({
        targets: this,
        scaleX: { to: 0 },
        scaleY: { to: 0 },
        duration: 150,
        ease: 'Back.easeIn',
        onComplete: () => {
          this.setVisible(false);
          this.onCancel?.();
        },
      });
    });
  }

  setOnSelectTower(cb: (baseId: string, type: TowerType) => void): void {
    this.onSelectTower = cb;
  }

  setOnCancel(cb: () => void): void {
    this.onCancel = cb;
  }

  destroy(): void {
    this.optionNodes.forEach(node => node.destroy());
    super.destroy();
  }
}

// Individual radial node for each tower option
class RadialNode extends Phaser.GameObjects.Container {
  private option: TowerOption;
  private nodeAngle: number;
  private radius: number;
  private background: Phaser.GameObjects.Graphics;
  private icon?: Phaser.GameObjects.Text;
  private nameText?: Phaser.GameObjects.Text;
  private costText?: Phaser.GameObjects.Text;
  private expanded = false;
  private hoverScale = 1;
  private onSelect?: (type: TowerType) => void;

  constructor(
    scene: Phaser.Scene,
    option: TowerOption,
    angle: number,
    radius: number,
    onSelect?: (type: TowerType) => void
  ) {
    super(scene, 0, 0);
    this.option = option;
    this.nodeAngle = angle;
    this.radius = radius;
    this.onSelect = onSelect;

    // Start at center
    this.setPosition(0, 0);
    this.setAlpha(0);

    // Create background circle
    this.background = scene.add.graphics();
    this.add(this.background);

    // Create content
    this.createContent();

    // Setup interaction
    this.setupInteraction();
  }

  private createContent(): void {
    const size = 36;

    // Background
    this.background.fillStyle(this.option.color, 0.9);
    this.background.fillCircle(0, 0, size);
    this.background.lineStyle(2, 0xffffff, 0.3);
    this.background.strokeCircle(0, 0, size);

    // First letter as icon
    this.icon = this.scene.add.text(0, -4, this.option.name[0] || '?', {
      fontSize: '18px',
      color: '#ffffff',
      fontFamily: 'monospace',
      fontStyle: 'bold',
    }).setOrigin(0.5);
    this.add(this.icon);

    // Name below
    this.nameText = this.scene.add.text(0, size + 8, this.option.name, {
      fontSize: '11px',
      color: '#ffffff',
      fontFamily: 'monospace',
    }).setOrigin(0.5);
    this.nameText.setAlpha(0);
    this.add(this.nameText);

    // Cost
    this.costText = this.scene.add.text(0, size + 22, `${this.option.cost}₣`, {
      fontSize: '10px',
      color: '#ffd700',
      fontFamily: 'monospace',
      fontStyle: 'bold',
    }).setOrigin(0.5);
    this.costText.setAlpha(0);
    this.add(this.costText);
  }

  private setupInteraction(): void {
    const hitArea = this.scene.add.circle(0, 0, 40, 0x000000, 0);
    hitArea.setInteractive({ useHandCursor: true });

    hitArea.on('pointerover', () => {
      if (!this.expanded) return;
      this.hoverScale = 1.15;
      this.scene.tweens.add({
        targets: this,
        scaleX: this.hoverScale,
        scaleY: this.hoverScale,
        duration: 100,
        ease: 'Quad.easeOut',
      });
    });

    hitArea.on('pointerout', () => {
      if (!this.expanded) return;
      this.hoverScale = 1;
      this.scene.tweens.add({
        targets: this,
        scaleX: 1,
        scaleY: 1,
        duration: 100,
        ease: 'Quad.easeOut',
      });
    });

    hitArea.on('pointerdown', () => {
      if (!this.expanded) return;
      this.scene.sound.play('click');
      // Pulse effect
      this.scene.tweens.add({
        targets: this,
        scaleX: 0.9,
        scaleY: 0.9,
        duration: 50,
        yoyo: true,
        ease: 'Quad.easeInOut',
        onComplete: () => {
          this.onSelect?.(this.option.type);
        },
      });
    });

    this.add(hitArea);
  }

  expand(): void {
    this.expanded = true;
    const targetX = Math.cos(this.nodeAngle) * this.radius;
    const targetY = Math.sin(this.nodeAngle) * this.radius;

    this.scene.tweens.add({
      targets: this,
      x: { from: 0, to: targetX },
      y: { from: 0, to: targetY },
      alpha: { from: 0, to: 1 },
      duration: 300,
      ease: 'Back.easeOut',
    });

    // Show labels with delay
    this.scene.time.delayedCall(150, () => {
      this.scene.tweens.add({
        targets: [this.nameText, this.costText],
        alpha: 1,
        duration: 150,
      });
    });
  }

  contract(): void {
    this.scene.tweens.add({
      targets: this,
      x: 0,
      y: 0,
      alpha: 0,
      duration: 200,
      ease: 'Back.easeIn',
    });
  }

  reset(): void {
    this.expanded = false;
    this.setPosition(0, 0);
    this.setAlpha(0);
    this.setScale(1);
    this.nameText?.setAlpha(0);
    this.costText?.setAlpha(0);
  }
}

export default TowerSelectPanel;
